// src/web-app/documents/documents-store.js
// Server-jobs-native document state for the web dashboard (plan Phase 2A.1).
//
// Merges three sources into one list:
//   - GET /documents            (registry: names, dates, verify_summary, report_count)
//   - GET /report-jobs?active   (in-flight verify/email jobs with stage progress)
//   - in-session uploads        (cards created on drop; nothing runs until Verify)
//
// Entry keys: registry/job entries are keyed by the server document_id. Files
// added this session get a unique LOCAL key immediately (no hashing on drop —
// a hash fallback once collapsed distinct files into one card) and are re-keyed
// to the server document_id when a job submission returns it.
//
// Every document action is a server job or a registry call — there is no
// client-side verify loop here (that lives only in the single-file detail view).

import { getSettingsForApiPayload } from '../../common/settings.js';
import { generateDocumentId } from '../../common/api.js';

const RECENT_PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 5000;
const POLL_JITTER_MS = 1500;
const POLL_TIMEOUT_MS = 30 * 60 * 1000;
const UPLOAD_CONCURRENCY = 2;
const CACHE_SUMMARY_CONCURRENCY = 3;
// If a job is still "queued" and has no progress shortly after creation, the
// task likely never dispatched. Do not resurrect those as permanent spinners.
const STALE_QUEUED_JOB_MS = 2 * 60 * 1000;
// Never re-attach to "active" batch items that haven't been touched in this
// long — they're leftovers of crashed/abandoned runs, not running jobs.
// (Alive jobs write progress every few seconds; the slowest silent phase is a
// fresh OCR, well under this.)
const STALE_JOB_MS = 30 * 60 * 1000;

const TERMINAL_ITEM_STATUSES = new Set(['completed', 'failed', 'error', 'cancelled']);

const isLiveItem = (item) => {
  if (!item || TERMINAL_ITEM_STATUSES.has(item.status)) return false;
  const stamp = item.progress?.updated_at || item.updated_at || item.created_at;
  const ts = Date.parse(stamp || '');
  if (!Number.isFinite(ts)) return false; // no timestamp -> treat as stale
  if ((item.status || 'queued') === 'queued' && !item.progress) {
    return Date.now() - ts < STALE_QUEUED_JOB_MS;
  }
  return Date.now() - ts < STALE_JOB_MS;
};

export class DocumentsStore {
  constructor({ apiBase, getAccessToken, signal }) {
    this.apiBase = apiBase;
    this.getAccessToken = getAccessToken;
    this.signal = signal;
    this.entries = new Map(); // key -> entry ({key, documentId|null, ...})
    this.listeners = new Set();
    this.pollTimers = new Map(); // batchId -> timeout handle
    this.uploadQueue = [];
    this.activeUploads = 0;
    this.cacheSummaryQueue = [];
    this.activeCacheSummaryFetches = 0;
    this.cacheSummaryRequested = new Set();
    this.seq = 0; // stable ordering + local key generation
    this.countedArtifacts = new Set(); // item_ids whose artifacts were tallied
    // Recency window: unfiled documents page in RECENT_PAGE_SIZE at a time
    // ("Show more" widens the window); filed/pinned docs always all load.
    this.recentLimit = RECENT_PAGE_SIZE;
    this.hasMoreRecent = false;

    signal?.addEventListener('abort', () => {
      for (const timer of this.pollTimers.values()) clearTimeout(timer);
      this.pollTimers.clear();
    });
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit() {
    for (const listener of this.listeners) {
      try { listener(this.getEntries()); } catch (err) { console.warn('[DocsStore] listener error:', err); }
    }
  }

  getEntries() {
    return Array.from(this.entries.values());
  }

  getEntry(key) {
    return this.entries.get(key) || null;
  }

  // Entry keyed by a server document_id (registry docs and job items).
  entryForDocument(documentId) {
    let entry = this.entries.get(documentId);
    if (!entry) {
      entry = { key: documentId, documentId, name: documentId, updatedAt: null, seq: ++this.seq };
      this.entries.set(documentId, entry);
    }
    return entry;
  }

  // Move a session entry from its local key onto the server document_id once a
  // submission reveals it. Merges into an existing registry entry when the
  // user re-uploaded a known document.
  rekeyEntry(localKey, documentId) {
    const entry = this.entries.get(localKey);
    if (!entry || !documentId || localKey === documentId) {
      if (entry) entry.documentId = entry.documentId || documentId || null;
      return entry;
    }
    const existing = this.entries.get(documentId);
    this.entries.delete(localKey);
    if (existing) {
      existing.name = entry.name || existing.name;
      existing.file = entry.file || existing.file;
      existing.session = existing.session || entry.session;
      existing.upload = entry.upload;
      existing.job = entry.job || existing.job;
      existing.seq = Math.min(existing.seq ?? Infinity, entry.seq ?? Infinity);
      return existing;
    }
    entry.key = documentId;
    entry.documentId = documentId;
    this.entries.set(documentId, entry);
    return entry;
  }

  async authHeaders(extra = {}) {
    const token = await this.getAccessToken();
    return { Authorization: `Bearer ${token}`, ...extra };
  }

  async fetchJson(path, options = {}) {
    const response = await fetch(`${this.apiBase}${path}`, {
      ...options,
      headers: { ...(await this.authHeaders()), ...(options.headers || {}) },
      cache: 'no-store',
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.detail || `${response.status} ${response.statusText}`);
    }
    return payload;
  }

  // === Rehydration (plan 2A.7): registry + active jobs ===

  async refresh() {
    const [docsResult, jobsResult] = await Promise.allSettled([
      this.fetchJson(`/documents?limit=${this.recentLimit}&include_organized=true`),
      this.fetchJson('/report-jobs?active=true&limit=50'),
    ]);

    if (docsResult.status === 'fulfilled') {
      this.hasMoreRecent = !!docsResult.value?.has_more;
      const seen = new Set();
      for (const doc of docsResult.value?.documents || []) {
        seen.add(doc.document_id);
        const entry = this.entryForDocument(doc.document_id);
        entry.name = doc.document_name || entry.name;
        entry.updatedAt = doc.updated_at || entry.updatedAt;
        entry.hasOcrCache = !!doc.has_ocr_cache;
        entry.reportCount = doc.report_count || 0;
        entry.lastVerifiedAt = doc.last_verified_at || null;
        entry.verifySummary = doc.verify_summary || entry.verifySummary || null;
        entry.partialChecks = doc.partial_checks || 0;
        entry.lastJobError = doc.last_job_error || null;
        entry.pinned = !!doc.pinned;
        entry.project = doc.project || null;
        entry.inRegistry = true;
        if (!entry.verifySummary && (doc.db_size_bytes || 0) > 0) {
          this.queueCacheSummaryHydration(doc.document_id);
        }
      }
      // Registry documents deleted elsewhere drop off unless they hold session state.
      for (const [key, entry] of this.entries) {
        if (entry.inRegistry && !seen.has(key) && !entry.job && !entry.upload) {
          this.entries.delete(key);
        }
      }
    } else {
      console.warn('[DocsStore] Failed to fetch documents:', docsResult.reason);
    }

    if (jobsResult.status === 'fulfilled') {
      for (const batch of jobsResult.value?.batches || []) {
        // Only re-attach to items that are demonstrably alive; stale "queued"/
        // "processing" leftovers would otherwise resurrect as phantom cards
        // and get polled forever on every page load.
        const liveItems = (batch?.items || []).filter(isLiveItem);
        if (!liveItems.length) continue;
        this.applyBatchStatus({ ...batch, items: liveItems }, { startPolling: true });
      }
    } else {
      console.warn('[DocsStore] Failed to fetch active jobs:', jobsResult.reason);
    }

    this.emit();
  }

  // Widen the recent-documents window by one page and re-sync the registry.
  async loadMoreRecent() {
    this.recentLimit += RECENT_PAGE_SIZE;
    return this.refresh();
  }

  queueCacheSummaryHydration(documentId) {
    if (!documentId || this.cacheSummaryRequested.has(documentId)) return;
    this.cacheSummaryRequested.add(documentId);
    this.cacheSummaryQueue.push(documentId);
    this.pumpCacheSummaryHydration();
  }

  pumpCacheSummaryHydration() {
    while (this.activeCacheSummaryFetches < CACHE_SUMMARY_CONCURRENCY && this.cacheSummaryQueue.length > 0) {
      const documentId = this.cacheSummaryQueue.shift();
      this.activeCacheSummaryFetches += 1;
      this.hydrateOneCacheSummary(documentId)
        .catch((err) => console.warn('[DocsStore] Cache summary hydration failed:', documentId, err))
        .finally(() => {
          this.activeCacheSummaryFetches -= 1;
          this.pumpCacheSummaryHydration();
        });
    }
  }

  async hydrateOneCacheSummary(documentId) {
    const payload = await this.fetchJson(`/documents/${encodeURIComponent(documentId)}`);
    const entry = this.entries.get(documentId);
    if (!entry) return;
    if (payload.verify_summary) {
      entry.verifySummary = payload.verify_summary;
      entry.lastVerifiedAt = payload.last_verified_at || entry.lastVerifiedAt;
      entry.updatedAt = payload.updated_at || entry.updatedAt;
    }
    entry.reportCount = payload.report_count || entry.reportCount || 0;
    entry.hasOcrCache = !!payload.has_ocr_cache;
    entry.partialChecks = payload.partial_checks || entry.partialChecks || 0;
    entry.lastJobError = payload.last_job_error || entry.lastJobError || null;
    this.emit();
  }

  // === Uploads & job submission (plan 2A.2) ===
  // Dropping files only ADDS them (state "ready"); nothing runs until the user
  // clicks Verify / Email report / Verify all. Session-added entries carry
  // `session: true` so the view can separate them from historical documents.

  async addFiles(files) {
    const pdfs = Array.from(files || []).filter((f) => f && /\.pdf$/i.test(f.name || ''));
    for (const file of pdfs) {
      const key = `local_${++this.seq}`;
      this.entries.set(key, {
        key,
        documentId: null,
        name: file.name || 'document.pdf',
        updatedAt: new Date().toISOString(),
        seq: this.seq,
        file,
        session: true,
        upload: { status: 'ready' },
      });
      this.attachKnownDocumentId(key, file).catch((err) => {
        console.warn('[DocsStore] Could not identify dropped file:', err);
      });
    }
    if (pdfs.length) this.emit();
    return pdfs.length;
  }

  async attachKnownDocumentId(localKey, file) {
    if (!file || !this.entries.has(localKey)) return null;
    const bytes = await file.arrayBuffer();
    const documentId = await generateDocumentId(bytes);
    const entry = this.rekeyEntry(localKey, documentId);
    if (!entry) return null;

    entry.documentId = documentId;
    entry.file = file;
    entry.session = true;
    entry.name = entry.name || file.name || documentId;
    entry.updatedAt = entry.updatedAt || new Date().toISOString();
    if (entry.verifySummary || (entry.partialChecks || 0) > 0 || (entry.reportCount || 0) > 0) {
      entry.upload = null;
    } else if (!entry.upload || entry.upload.status === 'ready') {
      entry.upload = { status: 'ready' };
    }
    this.emit();
    return documentId;
  }

  submit(key, { email = false, includeInline = true } = {}) {
    const entry = this.entries.get(key);
    if (!entry?.file) return false;
    const busy = entry.upload?.status === 'uploading' || entry.upload?.status === 'queued_local'
      || (entry.job && !TERMINAL_ITEM_STATUSES.has(entry.job.status));
    if (busy) return false;
    entry.upload = { status: 'queued_local' };
    this.uploadQueue.push({ key, email, includeInline });
    this.emit();
    this.pumpUploads();
    return true;
  }

  pumpUploads() {
    while (this.activeUploads < UPLOAD_CONCURRENCY && this.uploadQueue.length > 0) {
      const task = this.uploadQueue.shift();
      this.activeUploads += 1;
      this.runUpload(task).finally(() => {
        this.activeUploads -= 1;
        this.pumpUploads();
      });
    }
  }

  async runUpload({ key, email, includeInline }) {
    let entry = this.entries.get(key);
    const file = entry?.file;
    if (!entry || !file) return;

    entry.upload = { status: 'uploading' };
    entry.job = null;
    this.emit();

    try {
      const payload = await this.submitJob(file, { email, includeInline });
      entry = this.rekeyEntry(key, payload.document_id) || entry;
      entry.upload = null;
      entry.job = {
        batchId: payload.batch_id,
        itemId: payload.item_id,
        status: 'queued',
        workflow: email ? 'email_report' : 'verify_only',
        progress: null,
        error: null,
      };
      this.emit();
      this.watchBatch(payload.batch_id);
    } catch (err) {
      console.error('[DocsStore] Job submit failed:', err);
      // No silent fallback (plan 2A.3): a failed submit is a visible failed
      // card with Retry, never a behavior change.
      entry.upload = { status: 'submit_failed', error: err.message || String(err) };
      this.emit();
    }
  }

  async submitJob(file, { email = false, includeInline = true } = {}) {
    const token = await this.getAccessToken();
    const form = new FormData();
    form.append('file', file, file.name || 'document.pdf');
    // Both workflows render + register reports server-side; the flag only
    // controls whether an email is sent when the run finishes.
    form.append('workflow', email ? 'email_report' : 'verify_only');
    form.append('outputs', JSON.stringify({ pdf: true, json: false, html: true, database: false }));
    // User model overrides / EU-ZDR coercion must travel (plan Key fact 7).
    form.append('config', JSON.stringify({
      include_inline: !!includeInline,
      internet_policy: 'smart',
      concurrency: 8,
      data_source: 'internet',
      ...getSettingsForApiPayload(),
    }));

    const response = await fetch(`${this.apiBase}/report-jobs`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.detail || `Job submission failed (${response.status})`);
    }
    return payload;
  }

  pendingEntries() {
    return Array.from(this.entries.values()).filter((entry) => {
      const pending = entry.upload?.status === 'ready' || entry.upload?.status === 'submit_failed';
      return pending && !!entry.file && !entry.verifySummary;
    });
  }

  verifyAllPending({ email = false } = {}) {
    let submitted = 0;
    for (const entry of this.pendingEntries()) {
      if (this.submit(entry.key, { email })) submitted += 1;
    }
    return submitted;
  }

  // === Job progress (plan 2A.3): polling now, Firestore listener later ===
  // watchBatch is the single abstraction the dashboard depends on; swapping the
  // transport must not change anything above this line.

  watchBatch(batchId) {
    if (this.pollTimers.has(batchId) || this.signal?.aborted) return;
    const startedAt = Date.now();

    const poll = async () => {
      this.pollTimers.delete(batchId);
      if (this.signal?.aborted) return;
      let done = false;
      try {
        const batch = await this.fetchJson(`/report-jobs/${encodeURIComponent(batchId)}`);
        done = this.applyBatchStatus(batch);
        this.emit();
      } catch (err) {
        console.warn('[DocsStore] Poll failed for', batchId, err);
      }
      if (done) {
        // Pick up the authoritative registry state (report_count,
        // verify_summary, OCR flags) written by the completed job.
        this.refresh().catch(() => {});
        return;
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        this.markBatchTimedOut(batchId);
        this.emit();
        return;
      }
      const delay = POLL_INTERVAL_MS + Math.random() * POLL_JITTER_MS;
      this.pollTimers.set(batchId, setTimeout(poll, delay));
    };

    this.pollTimers.set(batchId, setTimeout(poll, POLL_INTERVAL_MS));
  }

  // Apply a /report-jobs batch payload to entries. Returns true when every
  // item is terminal (polling can stop).
  applyBatchStatus(batch, { startPolling = false } = {}) {
    let allTerminal = true;
    for (const item of batch?.items || []) {
      const entry = this.entryForDocument(item.document_id);
      entry.job = {
        batchId: batch.batch_id,
        itemId: item.item_id,
        status: item.status,
        workflow: batch.workflow || 'verify_only',
        progress: item.progress || null,
        error: item.error || null,
        cancelRequested: !!item.cancel_requested,
      };
      if (TERMINAL_ITEM_STATUSES.has(item.status)) {
        if (item.status === 'completed') {
          const summary = item.result_ref?.summary;
          if (summary) {
            entry.verifySummary = summary;
            entry.lastVerifiedAt = item.updated_at || new Date().toISOString();
            entry.updatedAt = entry.lastVerifiedAt;
          }
          const artifacts = item.result_ref?.artifacts || [];
          if (artifacts.length > 0 && !this.countedArtifacts.has(item.item_id)) {
            // Reports are rendered by every completed run; "Email report" can
            // now send them without re-verifying anything. Optimistic bump —
            // the registry refresh on completion supplies the real count.
            this.countedArtifacts.add(item.item_id);
            entry.reportCount = (entry.reportCount || 0) + artifacts.length;
          }
          entry.inRegistry = true;
        }
      } else {
        allTerminal = false;
      }
    }
    if (!allTerminal && startPolling) this.watchBatch(batch.batch_id);
    return allTerminal;
  }

  // Ask the server to stop a running/queued job. Optimistic "Stopping…" state;
  // the poll loop picks up the terminal "cancelled" status when the job lands.
  async cancelJob(key) {
    const entry = this.entries.get(key);
    const job = entry?.job;
    if (!job?.batchId || !job?.itemId || TERMINAL_ITEM_STATUSES.has(job.status)) return null;
    entry.job = { ...job, cancelRequested: true };
    this.emit();
    try {
      const payload = await this.fetchJson(
        `/report-jobs/${encodeURIComponent(job.batchId)}/items/${encodeURIComponent(job.itemId)}/cancel`,
        { method: 'POST' },
      );
      if (payload.status === 'cancelled') {
        entry.job = { ...entry.job, status: 'cancelled' };
        this.emit();
      }
      return payload;
    } catch (err) {
      entry.job = { ...entry.job, cancelRequested: false };
      this.emit();
      throw err;
    }
  }

  markBatchTimedOut(batchId) {
    for (const entry of this.entries.values()) {
      if (entry.job?.batchId === batchId && !TERMINAL_ITEM_STATUSES.has(entry.job.status)) {
        entry.job = { ...entry.job, status: 'failed', error: 'Timed out waiting for the job to finish.' };
      }
    }
  }

  // === Workspace organization (pin to active zone / project grouping) ===

  // Distinct project names across all known documents, for the assign menu.
  listProjects() {
    const names = new Set();
    for (const entry of this.entries.values()) {
      if (entry.project) names.add(entry.project);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }

  // PATCH pinned/project on the registry entry. Optimistic: the UI moves
  // immediately and reverts if the server rejects the change.
  async updateOrganization(key, fields) {
    const entry = this.entries.get(key);
    if (!entry?.documentId) throw new Error('Document is not saved on the server yet.');
    const before = { pinned: entry.pinned, project: entry.project };
    if ('pinned' in fields) entry.pinned = !!fields.pinned;
    if ('project' in fields) entry.project = fields.project || null;
    this.emit();
    try {
      const payload = await this.fetchJson(`/documents/${encodeURIComponent(entry.documentId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      entry.pinned = !!payload.pinned;
      entry.project = payload.project || null;
      entry.inRegistry = true;
      this.emit();
      return payload;
    } catch (err) {
      entry.pinned = before.pinned;
      entry.project = before.project;
      this.emit();
      throw err;
    }
  }

  // Demote a card out of the active zone: clears the (local) session flag,
  // any finished job (a terminal job otherwise keeps the card active for the
  // rest of the session), and the (persisted) pin. Only needs a server
  // round-trip when the doc was pinned.
  async demote(key) {
    const entry = this.entries.get(key);
    if (!entry) return;
    entry.session = false;
    if (entry.job && TERMINAL_ITEM_STATUSES.has(entry.job.status)) {
      entry.job = null;
    }
    if (entry.pinned && entry.documentId) {
      await this.updateOrganization(key, { pinned: false });
    } else {
      entry.pinned = false;
      this.emit();
    }
  }

  // === Document actions (need a server documentId) ===

  async emailExistingReports(key) {
    const entry = this.entries.get(key);
    if (!entry?.documentId) throw new Error('Document has no server-side reports yet.');
    return this.fetchJson('/report-jobs/email-existing', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_id: entry.documentId }),
    });
  }

  async fetchReports(key) {
    const entry = this.entries.get(key);
    if (!entry?.documentId) return [];
    const payload = await this.fetchJson(`/documents/${encodeURIComponent(entry.documentId)}/reports`);
    return payload?.reports || [];
  }

  async fetchReportContent(key, reportId) {
    const entry = this.entries.get(key);
    if (!entry?.documentId || !reportId) throw new Error('Report is not available.');
    const response = await fetch(
      `${this.apiBase}/documents/${encodeURIComponent(entry.documentId)}/reports/${encodeURIComponent(reportId)}/content`,
      {
        headers: await this.authHeaders(),
        cache: 'no-store',
      },
    );
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.detail || `Could not open report (${response.status})`);
    }
    return response.blob();
  }

  // Enable sharing for one report (optionally password-protected). Returns
  // { shared, share_url, has_password, ... }.
  async shareReport(key, reportId, { password } = {}) {
    const entry = this.entries.get(key);
    if (!entry?.documentId) throw new Error('Document has no server-side reports yet.');
    return this.fetchJson(
      `/documents/${encodeURIComponent(entry.documentId)}/reports/${encodeURIComponent(reportId)}/share`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: password || null }),
      },
    );
  }

  // Disable sharing for one report (revokes the link immediately).
  async revokeReportShare(key, reportId) {
    const entry = this.entries.get(key);
    if (!entry?.documentId) throw new Error('Document has no server-side reports yet.');
    return this.fetchJson(
      `/documents/${encodeURIComponent(entry.documentId)}/reports/${encodeURIComponent(reportId)}/share`,
      { method: 'DELETE' },
    );
  }

  async deleteDocument(key) {
    const entry = this.entries.get(key);
    if (entry?.documentId) {
      await this.fetchJson(`/documents/${encodeURIComponent(entry.documentId)}`, { method: 'DELETE' });
    }
    this.entries.delete(key);
    this.emit();
  }
}
